// Dev-only auto-reload when the device WASM is republished under an open tab.
//
// In dev the wasm is pinned to a STABLE url (/wasm/latest/), so after a
// `make device-wasm` republish the bytes at that url change but an already-open
// page keeps running the old wasm it instantiated on the main thread. This
// watcher polls the VERSION beacon that `make device-wasm` writes alongside
// device.wasm and reloads the page when it changes, so a wasm rebuild is picked
// up without a manual refresh. The key-custody Service Worker keeps the device
// unlocked across the reload, so the only cost is transient in-page state.
//
// No-op in production — ENFORCED via NODE_ENV, not merely the version pin.
// DEVICE_WASM_VERSION is committed as 'latest', so a prod build that forgot to
// pin would still read 'latest'; gating only on the pin would then leave every
// production user's tab polling and self-reloading mid-task on any republish
// (hostile). NODE_ENV !== 'production' is the real invariant; the 'latest' pin is
// a secondary "nothing to poll once fingerprinted" condition.

import { deviceWasmVersion, wasmBase } from './loader'

const POLL_INTERVAL_MS = 2500

// Pure predicate (unit-testable): the watcher runs ONLY in a development bundle
// with the dev 'latest' pin. NODE_ENV is read directly (Webpack DefinePlugin
// inlines it; no `typeof process` guard — that's the documented Gatsby trap).
export function shouldStartDevWasmReloadWatcher(nodeEnv: string | undefined, version: string): boolean {
    return nodeEnv !== 'production' && version === 'latest'
}

async function fetchPublishedVersion(): Promise<string | null> {
    try {
        const res = await fetch(`${wasmBase}/VERSION`, { cache: 'no-cache' })
        if (!res.ok) return null
        return (await res.text()).trim()
    } catch {
        return null
    }
}

let started = false

export function startDevWasmReloadWatcher(): void {
    if (started || typeof window === 'undefined') return
    // ENFORCED prod-off: never run in a production bundle, even if the version
    // pin is still 'latest' (see header).
    if (!shouldStartDevWasmReloadWatcher(process.env.NODE_ENV, deviceWasmVersion)) return
    started = true

    let bootVersion: string | null = null
    let reloading = false

    const check = async (): Promise<void> => {
        if (reloading) return
        const current = await fetchPublishedVersion()
        // Beacon missing/unreachable (e.g. before the api+site emit it): stay
        // put — never reload on uncertainty.
        if (current == null) return
        if (bootVersion == null) {
            bootVersion = current
            return
        }
        if (current !== bootVersion) {
            reloading = true
            // eslint-disable-next-line no-console
            console.info(
                `[dev] device wasm changed (${bootVersion} → ${current}); reloading to pick it up…`,
            )
            window.location.reload()
        }
    }

    void check() // capture the boot version immediately
    window.setInterval(() => void check(), POLL_INTERVAL_MS)
    // The common case is rebuilding in a terminal then switching back to the
    // browser, so also check whenever the tab regains focus.
    window.addEventListener('focus', () => void check())
}
